import {
    collection,
    doc,
    getDocs,
    onSnapshot,
    query,
    updateDoc,
    where,
    Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { notify, notifyMany } from "./notify";
import { customOrderByDESC } from "./helper";

/**
 * Get userIds of committee members with finance area for a community.
 */
const getFinanceCommitteeIds = async (communityCode) => {
    const snap = await getDocs(
        query(
            collection(db, "community_body"),
            where("communityCode", "==", communityCode)
        )
    );
    const ids = [];
    snap.forEach((d) => {
        const member = d.data();
        const areas = member.areas || [];
        if (areas.includes("finance") || areas.length === 5) {
            if (member.userId) ids.push(member.userId);
        }
    });
    return ids;
};

/**
 * Build the Firestore update object for recording a payment on a bill.
 * Shared by saveTransaction & saveCashTransaction to keep bill-update logic DRY.
 *
 * @param {object} billData — current bill document data
 * @param {string} transactionId — the newly created transaction ID
 * @param {number} paymentAmt — amount being paid toward this bill
 * @param {object} opts — { paymentMode, referenceNo, receiptUrl }
 * @returns {object} Firestore update fields for the bill doc
 */
export const buildBillPaymentUpdate = (billData, transactionId, paymentAmt, opts = {}) => {
    const billTotal = Number(billData.amount || 0);
    const prevPaid = Number(billData.paidAmount || 0);
    const newPaid = prevPaid + paymentAmt;
    const isFullyPaid = newPaid >= billTotal;

    const paymentRecord = {
        transactionId,
        amount: paymentAmt,
        paymentMode: opts.paymentMode || "",
        timestamp: Timestamp.now(),
    };
    if (opts.referenceNo) paymentRecord.referenceNo = opts.referenceNo;
    if (opts.receiptUrl) paymentRecord.receiptUrl = opts.receiptUrl;

    const update = {
        paidAmount: newPaid,
        payments: [...(billData.payments || []), paymentRecord],
    };
    // Only set transactionId when fully paid — keeps existing paid/unpaid queries working
    if (isFullyPaid) update.transactionId = transactionId;
    if (opts.paymentMode) update.paymentMode = opts.paymentMode;
    if (opts.receiptUrl) update.receiptUrl = opts.receiptUrl;
    if (opts.referenceNo) update.referenceNo = opts.referenceNo;

    return update;
};

export const getPaymentsInRealTime = (communityCode, flats, cb) => {
    if (flats && flats.length > 0) {
        return onSnapshot(
            query(collection(db, "bills"), where("communityCode", "==", communityCode), where("componentType", "==", 'revenue'), where("flatId", "in", flats), where("transactionId", "==", "")),
            (doc) => {
                const dataSnaps = [];
                doc.forEach((each) => {
                    const temp = each.data();
                    const object = {
                        ...temp,
                        temp: temp.timestamp.seconds,
                    };
                    dataSnaps.push(object);
                });
                cb(customOrderByDESC(dataSnaps));
            }
        );
    }
};

export const getBillsInRealTime = (communityCode, cb) => {
    return onSnapshot(
        query(collection(db, "bills"), where("communityCode", "==", communityCode), where("componentType", "==", 'expense'), where("transactionId", "==", "")),
        (doc) => {
            const dataSnaps = [];
            doc.forEach((each) => {
                const temp = each.data();
                const object = {
                    ...temp,
                    temp: temp.timestamp.seconds,
                };
                dataSnaps.push(object);
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};

export const getReceivedPaymentsInRealTime = (communityCode, flats, cb) => {
    if (flats && flats.length > 0) {
        return onSnapshot(
            query(collection(db, "bills"), where("communityCode", "==", communityCode), where("componentType", "==", 'revenue'), where("flatId", "in", flats), where("transactionId", '!=', '')),
        (doc) => {
            const dataSnaps = [];
            doc.forEach((each) => {
                const temp = each.data();
                const object = {
                    ...temp,
                    temp: temp.timestamp.seconds,
                };
                dataSnaps.push(object);
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
    }
};

export const getRevenueBillsInRealTime = (communityCode, cb) => {
    return onSnapshot(
        query(collection(db, "bills"), where("communityCode", "==", communityCode), where("componentType", "==", 'revenue')),
        (doc) => {
            const dataSnaps = [];
            doc.forEach((each) => {
                const temp = each.data();
                const object = {
                    ...temp,
                    temp: temp.timestamp.seconds,
                };
                dataSnaps.push(object);
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};

export const getClearedBillsInRealTime = (communityCode, cb) => {
    return onSnapshot(
        query(collection(db, "bills"), where("communityCode", "==", communityCode), where("componentType", "==", 'expense'), where("transactionId", '!=', '')),
        (doc) => {
            const dataSnaps = [];
            doc.forEach((each) => {
                const temp = each.data();
                const object = {
                    ...temp,
                    temp: temp.timestamp.seconds,
                };
                dataSnaps.push(object);
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};

/* ── Submit a payment claim on a bill (pending treasurer review) ── */
export const submitPaymentClaim = async (billId, claim, meta) => {
    await updateDoc(doc(db, "bills", billId), {
        pendingClaim: {
            ...claim,
            status: "pending",
            claimedAt: Timestamp.now(),
        },
    });

    // Notify finance committee members
    let recipientIds = meta?.treasurerIds || [];
    if (recipientIds.length === 0 && meta?.communityCode) {
        recipientIds = await getFinanceCommitteeIds(meta.communityCode);
    }
    if (recipientIds.length > 0) {
        notifyMany({
            recipientIds,
            category: "finance",
            title: "Payment Claim Submitted",
            body: `₹${claim.claimAmount || ""} claim submitted for ${meta?.billName || "a bill"}`,
            data: { billId },
        }).catch(() => {});
    }
};

/**
 * Submit payment claims on multiple bills at once (bulk payment).
 * Each bill gets its own pendingClaim, linked by a shared batchId.
 *
 * @param {Array<{billId: string, claimAmount: number}>} billEntries
 * @param {Object} baseClaim – shared claim fields (paymentMode, claimedBy, etc.)
 */
export const submitBulkPaymentClaim = async (billEntries, baseClaim, meta) => {
    const batchId = `batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const promises = billEntries.map(({ billId, claimAmount }) =>
        updateDoc(doc(db, "bills", billId), {
            pendingClaim: {
                ...baseClaim,
                claimAmount: Number(claimAmount),
                batchId,
                status: "pending",
                claimedAt: Timestamp.now(),
            },
        })
    );
    await Promise.all(promises);

    // Notify finance committee members
    if (meta?.communityCode) {
        const totalAmount = billEntries.reduce((s, e) => s + Number(e.claimAmount || 0), 0);
        const recipientIds = await getFinanceCommitteeIds(meta.communityCode);
        if (recipientIds.length > 0) {
            notifyMany({
                recipientIds,
                category: "finance",
                title: "Bulk Payment Claim Submitted",
                body: `₹${totalAmount.toLocaleString()} bulk claim submitted across ${billEntries.length} bill(s)`,
                data: { batchId },
            }).catch(() => {});
        }
    }

    return batchId;
};

/* ── Reject a payment claim — bill returns to "due" ── */
export const rejectPaymentClaim = async (billId, rejectedBy, reason, meta) => {
    await updateDoc(doc(db, "bills", billId), {
        pendingClaim: {
            status: "rejected",
            rejectedBy,
            rejectedAt: Timestamp.now(),
            reason: reason || "",
        },
    });
    if (meta?.claimantId) {
        notify({
            recipientId: meta.claimantId,
            category: "finance",
            title: "Payment Claim Rejected",
            body: `Your claim for ${meta.billName || "a bill"} was rejected${reason ? ": " + reason : ""}`,
            data: { billId },
        }).catch(() => {});
    }
};

/* ── Mark claim as approved (after account is credited) ── */
export const clearPaymentClaim = async (billId, meta) => {
    await updateDoc(doc(db, "bills", billId), {
        "pendingClaim.status": "approved",
    });
    if (meta?.claimantId) {
        notify({
            recipientId: meta.claimantId,
            category: "finance",
            title: "Payment Approved",
            body: `Your payment claim for ${meta.billName || "a bill"} has been approved`,
            data: { billId },
        }).catch(() => {});
    }
};

/* ── Listener: bills with a pending payment claim awaiting review ── */
export const getPendingClaimsInRealTime = (communityCode, cb) => {
    return onSnapshot(
        query(
            collection(db, "bills"),
            where("communityCode", "==", communityCode),
            where("pendingClaim.status", "==", "pending")
        ),
        (snap) => {
            const dataSnaps = [];
            snap.forEach((each) => {
                const temp = each.data();
                dataSnaps.push({ ...temp, temp: temp.timestamp.seconds });
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};

/* ── Listener: all bills linked to a specific account head ── */
export const getBillsByAccountHeadInRealTime = (communityCode, accountHeadId, cb) => {
    return onSnapshot(
        query(
            collection(db, "bills"),
            where("communityCode", "==", communityCode),
            where("accountHeadId", "==", accountHeadId)
        ),
        (snap) => {
            const dataSnaps = [];
            snap.forEach((each) => {
                const temp = each.data();
                dataSnaps.push({ ...temp, temp: temp.timestamp.seconds });
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};